import { Alert, Box, Button, Container, Stack } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { MemorizedPagedRepeat } from "lib/@components/memorized-paged-repeat"
import { NoResults, NoResultsContainer } from "lib/@components/no-results/no-results"
import { useReference } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { navigate } from "lib/routes/navigate"
import { uniqueId } from "library/database/split-id.mjs"
import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"
import { findInChildren } from "library/tree/index.mjs"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { FacilitiesSearchResult } from "routes/facilities/facilities-search-result"
import { LIVE_TREE, READY_TREE } from "library/constants.mjs"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { handleUnlicensedContent } from "routes/schedule/lib/handle-unlicensed-content"

export function FacilitiesSearch() {
    const [share] = useSearchParam("share")
    const live = useTreeItem(LIVE_TREE)
    const ready = useTreeItem(READY_TREE)
    const [{ searchResults }] = useReference({ searchResults: {} })
    const trees = [live, ready].filter(Boolean)
    const results = (searchResults.schedules ?? []).filter(Boolean).map(toSearchResult)

    if (share && !live) return <LoadingScreen description="Share Link Search" />

    if (!trees.length) {
        return (
            <NoResultsContainer message="There are no longer any valid Maintenance Regimes available using this link." />
        )
    }

    return (
        <PageBreadcrumbContainer title="Search Results">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Stack spacing={2}>
                    <Alert sx={{ "& .MuiAlert-message": { width: 1 } }} severity="info">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box flex={1}>{getSearchMessage()}</Box>
                            <Button variant="outlined" color="primary" size="small" onClick={goBack}>
                                Back
                            </Button>
                        </Stack>
                    </Alert>
                    <Bound onClickSchedule={open}>
                        <MemorizedPagedRepeat
                            showTop
                            showBottom
                            id="facilities-search-results"
                            list={results}
                            pageSize={10}
                            fallback={<NoResults />}
                            item={<FacilitiesSearchResult />}
                        />
                    </Bound>
                </Stack>
            </Container>
        </PageBreadcrumbContainer>
    )

    function getSearchMessage() {
        if (!searchResults.query) return "Search results will appear here after you run a search."
        return `Search for "${searchResults.query}" found ${searchResults.count ?? results.length} schedules when it ran ${Date.create(
            searchResults.time
        ).relative()}.`
    }

    function toSearchResult(schedule) {
        const treeItem = findTreeItem(schedule)
        return {
            schedule: {
                ...schedule,
                id: treeItem?.id ?? schedule.id,
                label: schedule.title,
                tags: schedule.coreTagDescriptions,
            },
            treeItem,
        }
    }

    function findTreeItem(schedule) {
        const scheduleId = normalizeScheduleId(schedule.id) ?? normalizeRecordId(schedule._id)
        if (!scheduleId) return undefined
        return trees.flatMap(findMatches).at(0)

        function matchesSchedule(item) {
            const itemScheduleId = normalizeScheduleId(item?.id)
            const itemRecordId = normalizeRecordId(item?._id)
            return itemScheduleId === scheduleId || itemRecordId === scheduleId
        }

        function findMatches(tree) {
            return findInChildren(tree, matchesSchedule)
        }
    }

    function open(schedule) {
        if (handleUnlicensedContent(schedule)) return
        const shareParam = encodeURIComponent(share)
        const versionParam = schedule.version ? `&version=${encodeURIComponent(schedule.version)}` : ""
        navigate(`/app/facilities/schedule/${schedule.id}?share=${shareParam}${versionParam}`)
    }

    function goBack() {
        navigate(`/app/facilities?share=${encodeURIComponent(share)}`)
    }
}

function normalizeScheduleId(id) {
    if (!id) return undefined
    return getScheduleRecordId(String(id))
}

function normalizeRecordId(id) {
    if (!id) return undefined
    return uniqueId(String(id))
}
